import type { ShorthandValue, ButtonProps } from '@archipro-design/aria';
import {
    Grid,
    Flex,
    Header,
    Button,
    useStyles,
    ReadMore,
    useAppDisplayModeContext,
    useTheme,
    pxToRem,
} from '@archipro-design/aria';
import { AboutBanner } from '../about-banner';
import { ABOUT_BANNER_SIZE } from '@modules/professional/config/constants';
import { ExternalLinkLine, HeartFill, HeartLine } from '@archipro-design/icons';
import { Check } from 'lucide-react';

import { CustomTabs } from '../custom-tabs/CustomTabs';
import * as S from './Info.style';

import { useLoaderData } from 'react-router';
import { useRef, useEffect, useState } from 'react';
import { useTracker } from '@archipro-website/tracker';
import { useCurrentTabBanner } from '@modules/professional/hook/use-current-tab-banner-image';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import type { InfoPageLoaderData } from '@modules/professional/api/info-page-loader.server';
import { useWebsiteTrackingProxy } from '~/modules/tracking/hook/useWebsiteTrackingProxy';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import { useWebsiteVisited } from '~/modules/tracking/hook/useWebsiteVisited';
import { useEnquirySubmitted } from '~/modules/tracking/hook/useEnquirySubmitted';
import type { ProfessionalBannersData } from '@modules/professional/page/OverviewPage';
import { useIsDisabledInPreview } from '~/modules/root/hook/use-is-disabled-in-preview';
import { FollowSelector } from '~/modules/moodboards/components';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { isUserLoggedIn, useUser } from '@modules/user';
import { useGuestSave } from '~/modules/moodboards/hooks/use-guest-save';

interface InfoProps {
    defaultClamped?: boolean;
    banners: ProfessionalBannersData;
}

const MAX_LINES = {
    MOBILE: 15,
    DESKTOP: 15,
};

const LINE_HEIGHT = 24;

export const Info: React.FC<InfoProps> = ({
    defaultClamped = true,
    banners,
}) => {
    const [contentClamped, setContentClamped] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const tracker = useTracker();
    const { mobile: isMobile } = useAppDisplayModeContext();
    const theme = useTheme();

    const { blockSummary, professional, libraryTabs } =
        useLoaderData<InfoPageLoaderData>();

    const {
        imageUrl: bannerImage,
        bannerId,
        wasUpscaled: bannerWasUpscaled,
    } = useCurrentTabBanner(banners, true);

    const hasAboutBanner = banners?.some((b) => b.bannerSegment === 'ABOUTUS');

    // Get full professional data for enquiry/save/website buttons
    const professionalData = useProfessionalInitialRouteData();

    const { isInDesignBoard } = useHandleFollow();
    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);
    const { guestSave } = useGuestSave();

    const { referralTrackingURL, referralTrackingParams } =
        useWebsiteReferralTracking(professionalData.Website, {
            libraryItemID: professionalData.ID,
            professionalID: professionalData.ID,
            professionalTitle: professionalData.Title,
        });

    const { hasVisited, markVisited } = useWebsiteVisited(professionalData.ID);
    const { hasSubmitted } = useEnquirySubmitted(professionalData.ID);

    const { openEnquiryModal } = useEnquiryModal({
        professional: professionalData,
        itemType: 'professional',
        siteTreeID: professionalData.ID,
        includeBranch: true,
    });

    const enquiryLabel =
        professionalData.primaryCTA?.primaryCTALabelTitleCase ?? 'Send Enquiry';

    const { css } = useStyles({
        isMobile,
        hasBannerImage: !!bannerImage,
        hasCustomTabs: !!libraryTabs?.length,
    });

    const myRef = useRef<HTMLInputElement | null>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const preventOpenLink = useIsDisabledInPreview('profile_social_links');

    const clickProxy = useWebsiteTrackingProxy(
        {
            professionalID: professional.id,
            professionalTitle: professional.title,
            itemType: 'Professional',
        },
        { preventOpenLink },
    );

    useEffect(() => {
        if (defaultClamped === false) {
            myRef?.current?.scrollIntoView();
        }
    }, [myRef, defaultClamped]);

    useEffect(() => {
        const content = contentRef.current;
        if (content) {
            const numOfLines = content.clientHeight / LINE_HEIGHT;
            const maxLines = isMobile ? MAX_LINES.MOBILE : MAX_LINES.DESKTOP;
            setContentClamped(numOfLines > maxLines);
        }
    }, [isMobile]);

    return (
        <Grid
            ref={myRef}
            columns={!isMobile ? `${pxToRem(ABOUT_BANNER_SIZE)} 1fr` : ''}
            className={css(S.InfoContainer)}
        >
            {!isMobile && (
                <div>
                    <AboutBanner
                        bannerImage={bannerImage}
                        bannerId={hasAboutBanner ? bannerId : null}
                        wasUpscaled={hasAboutBanner ? bannerWasUpscaled : false}
                        title={professional.title}
                    />
                </div>
            )}

            <Flex vAlign="start" column={true} className={css(S.InfoWrap)}>
                {!isMobile && (
                    <Header variant="03" className={css(S.AboutHeader)}>
                        About
                    </Header>
                )}

                {!!blockSummary && (
                    <>
                        {isMobile ? (
                            <ReadMore
                                maxLines={MAX_LINES.MOBILE}
                                content={
                                    <span
                                        ref={contentRef}
                                        key={`about-tab-${refreshKey}`}
                                        dangerouslySetInnerHTML={{
                                            __html: blockSummary,
                                        }}
                                        onClick={clickProxy}
                                        onContextMenu={clickProxy}
                                    />
                                }
                                readMoreText="Read more"
                                className={`${css(
                                    S.ReadMoreText,
                                    S.ButtonStyle,
                                    isDescriptionExpanded
                                        ? S.hideToggleButton
                                        : {},
                                )} cms-rich-text`}
                                defaultClampedValue={
                                    defaultClamped ? true : contentClamped
                                }
                                variables={{
                                    contentLineHeight: isMobile
                                        ? 1.5
                                        : theme.siteVariables.lineHeightSmall,
                                }}
                                toggleButton={
                                    {
                                        onClick: (_, clamped) => {
                                            if (clamped) {
                                                tracker.log(
                                                    'ProfessionalDescriptionExpand',
                                                    {
                                                        url: new URL(
                                                            window.location
                                                                .href,
                                                        ),
                                                        targetTracker:
                                                            'archiproTracker',
                                                    },
                                                );
                                            }
                                            setIsDescriptionExpanded(true);
                                            setRefreshKey((prev) => prev + 1);
                                        },
                                    } as ShorthandValue<ButtonProps>
                                }
                            />
                        ) : (
                            <div
                                className={`${css(S.ReadMoreText)} cms-rich-text`}
                                dangerouslySetInnerHTML={{
                                    __html: blockSummary,
                                }}
                                onClick={clickProxy}
                                onContextMenu={clickProxy}
                            />
                        )}
                    </>
                )}

                {isMobile && (
                    <Flex column className={css(S.ButtonContainer)}>
                        <div className={css(S.EnquireContainer)}>
                            <Flex>
                                <Button
                                    size={16}
                                    color="dark"
                                    className={css(S.EnquireButton)}
                                    onClick={() => {
                                        openEnquiryModal({
                                            title: enquiryLabel,
                                            ctaLabel: enquiryLabel,
                                        });
                                    }}
                                    variables={{
                                        darkButtonBackgroundColorNormal:
                                            '#222222',
                                        darkOutlinedButtonBorderNormal:
                                            '1px solid #222222',
                                    }}
                                >
                                    {enquiryLabel}
                                    {hasSubmitted && (
                                        <Check className="text-green-600 ml-2 inline-block h-4 w-4 animate-tick-pop-in" />
                                    )}
                                </Button>

                                {(() => {
                                    const isSaved = isInDesignBoard(
                                        professionalData.ID,
                                        'ProfessionalPin',
                                    );
                                    const heartButton = (
                                        <Button
                                            className={css(S.ButtonHeart)}
                                            transparent
                                            size={20}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                if (!isLoggedIn) {
                                                    guestSave({
                                                        pinId: professionalData.ID,
                                                        pinType:
                                                            'ProfessionalPin',
                                                    });
                                                }
                                            }}
                                            icon={
                                                isSaved ? (
                                                    <HeartFill size="2x" />
                                                ) : (
                                                    <HeartLine size="2x" />
                                                )
                                            }
                                            variables={{
                                                iconPadding: 0,
                                                buttonPadding: 0,
                                            }}
                                        />
                                    );
                                    return isLoggedIn ? (
                                        <FollowSelector
                                            trigger={heartButton}
                                            pinId={professionalData.ID}
                                            pinType="ProfessionalPin"
                                        />
                                    ) : (
                                        heartButton
                                    );
                                })()}
                            </Flex>
                        </div>
                        <Flex
                            gap="space-20"
                            className={css(S.SaveShareContainer)}
                            hAlign="center"
                            vAlign="start"
                        >
                            <Button
                                as={'a'}
                                href={referralTrackingURL}
                                target="_blank"
                                size={16}
                                color="dark"
                                transparent
                                outlined
                                variables={{
                                    buttonSize20LineHeight: '130%',
                                    buttonPadding: pxToRem(15),
                                }}
                                className={css(S.ViewWebsiteButton)}
                                {...referralTrackingParams}
                                onClick={markVisited}
                            >
                                VISIT WEBSITE
                                {hasVisited ? (
                                    <Check
                                        style={{
                                            width: pxToRem(16),
                                            height: pxToRem(16),
                                            marginLeft: pxToRem(8),
                                            marginTop: pxToRem(-2),
                                        }}
                                        className="text-green-600 animate-tick-pop-in"
                                    />
                                ) : (
                                    <ExternalLinkLine
                                        styles={{
                                            width: pxToRem(16),
                                            height: pxToRem(16),
                                            '& > svg': {
                                                marginLeft: pxToRem(8),
                                                marginTop: pxToRem(-2),
                                            },
                                        }}
                                    />
                                )}
                            </Button>
                        </Flex>
                    </Flex>
                )}

                <CustomTabs />
            </Flex>
        </Grid>
    );
};
